import type { EventListQuery } from '@lib/input/event-list.input'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { eventService } from '@/services/EventService'
import { planHistoryService } from '@/services/PlanHistoryService'
import { adsKeys, eventKeys, clientKeys, pipelineKeys, plannerConversionKeys, userKeys } from '@/hooks/queryKeys'
import type {
  EventDeleteResultDto,
  EventDto,
  EventDetailDto,
  EventListDto,
  EventPhaseDto,
  EventPlannerOptionListDto,
} from '@lib/dto/event.dto'
import type { AssignEventVendorInput } from '@lib/input/vendor.input'
import type { VenueVendorChoiceListDto } from '@lib/dto/vendor.dto'
import type { VendorService } from '@lib/enums/vendor-service.enum'
import type { ClientToPlannerInput } from '@lib/input/planner-conversion.input'
import type { ClientToPlannerDto } from '@lib/dto/planner-conversion.dto'
import type { VenueCalendarDto } from '@lib/dto/venue-calendar.dto'
import type { VenueCalendarQuery } from '@lib/input/venue-calendar.input'
import type { TrackAvailabilityQuery } from '@lib/input/track-availability.input'
import type { TrackAvailabilityListDto } from '@lib/dto/track-availability.dto'
import type { EmailLogListDto } from '@lib/dto/email-log.dto'
import type { EventEditorsDto } from '@lib/dto/plan-impact.dto'
import type { PlanRevisionListDto } from '@lib/dto/plan-revision.dto'
import type { VenuePurgePreviewDto, VenuePurgeResultDto } from '@lib/dto/venue-purge.dto'
import type { PurgeVenueEventsInput } from '@lib/input/venue-purge.input'
import type { FurnitureGalleryDto } from '@lib/dto/furniture-gallery.dto'
import type { UserDto, UserListDto } from '@lib/dto/user.dto'
import type {
  AddBroughtInInput,
  AddEventClientInput,
  ConfirmedPlansChoice,
  CreateEventInput,
  CreatePhaseInput,
  DeleteEventQuery,
  LoseEventInput,
  ReopenEventInput,
  RequestedDatesInput,
  HoldInput,
  UpdateBroughtInInput,
  UpdateEventInput,
  SetGuestCountInput,
  UpdatePhaseInput,
} from '@lib/input/event.input'
import type { ApplyEventImportInput, PreviewEventImportInput } from '@lib/input/event-import.input'
import type { EventImportApplyResultDto, EventImportPreviewDto } from '@lib/dto/event-import.dto'
import type { EventDocumentDto } from '@lib/dto/event-document.dto'
import type { CreateEventDocumentInput } from '@lib/input/event-document.input'
import { furnitureKeys, furnitureGroupKeys, vendorKeys, venueKeys } from '@/hooks/queryKeys'

/** Events visible to the current user (scoped by the API). */
export function useEvents(query: EventListQuery = {}, page?: number): UseQueryResult<EventListDto> {
  return useQuery({
    queryKey: eventKeys.list(query, page),
    queryFn: () => eventService.list(query, page),
    staleTime: 30_000,
    // The previous page's rows stay on screen while the next one loads — see
    // the note on `useVenues`.
    placeholderData: (previous) => previous,
  })
}

/** Full event detail (venue + phases) — what the editor loads. */
export function useEvent(id: string): UseQueryResult<EventDetailDto> {
  return useQuery({ queryKey: eventKeys.detail(id), queryFn: () => eventService.get(id), staleTime: 30_000 })
}

/**
 * Who else has each of the event's plans open, for a delete confirmation. Asked
 * fresh each time it opens: the answer is about this moment.
 */
export function useEventEditors(eventId: string, enabled: boolean): UseQueryResult<EventEditorsDto> {
  return useQuery({
    queryKey: eventKeys.editors(eventId),
    queryFn: () => eventService.editors(eventId),
    enabled,
    staleTime: 0,
  })
}

/**
 * The earlier versions kept of one plan. Asked fresh each time the history opens:
 * a version may have been kept in the last few seconds, and "what was it before?"
 * is the question the history is for.
 */
export function usePlanRevisions(eventId: string, phaseId: string, enabled: boolean): UseQueryResult<PlanRevisionListDto> {
  return useQuery({
    queryKey: eventKeys.planRevisions(eventId, phaseId),
    queryFn: () => planHistoryService.revisions(eventId, phaseId),
    enabled,
    staleTime: 0,
  })
}

/** The plans of an event that were deleted and are still kept. Fresh each time, for the reason above. */
export function useDeletedPlans(eventId: string, enabled: boolean): UseQueryResult<PlanRevisionListDto> {
  return useQuery({
    queryKey: eventKeys.deletedPlans(eventId),
    queryFn: () => planHistoryService.deletedPlans(eventId),
    enabled,
    staleTime: 0,
  })
}

/**
 * Put a kept version of a plan back. The event refreshes, and so do the lists of
 * versions: a restore keeps what it replaced, so the history has one more entry.
 */
export function usePlanRestore(eventId: string): UseMutationResult<EventDetailDto, Error, { revisionId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { revisionId: string }) => planHistoryService.restore(eventId, vars.revisionId),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.deletedPlans(eventId) })
      void queryClient.invalidateQueries({ queryKey: [...eventKeys.all, 'plan-revisions', eventId] })
    },
  })
}

/** Create an event, refreshing the list on success. */
export function useCreateEvent(): UseMutationResult<EventDto, Error, CreateEventInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEventInput) => eventService.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
    },
  })
}

/** The fields of an event a calendar shows; an edit to any other leaves calendars as they are. */
const CALENDAR_FIELDS = ['name', 'date', 'startsAt', 'endsAt', 'trackIds', 'guestCount'] as const

/**
 * Edit an event's own fields; updates the detail cache and refreshes the lists.
 * Calendars refresh when the edit is one they show: a new date moves the event
 * there, and booking an inquiry releases its hold.
 */
export function useUpdateEvent(eventId: string): UseMutationResult<EventDetailDto, Error, UpdateEventInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateEventInput) => eventService.update(eventId, input),
    onSuccess: (data, input) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      if (CALENDAR_FIELDS.some((field) => input[field] !== undefined)) {
        void queryClient.invalidateQueries({ queryKey: eventKeys.calendars() })
      }
      // What a HoneyBook booking is worth counts toward return on ad spend.
      if (input.bookedValueCents !== undefined) {
        void queryClient.invalidateQueries({ queryKey: pipelineKeys.all })
        void queryClient.invalidateQueries({ queryKey: adsKeys.all })
      }
    },
  })
}

/** Set the guest count, as the couple, their planner or staff; updates the detail cache and refreshes the lists. */
export function useSetGuestCount(eventId: string): UseMutationResult<EventDetailDto, Error, SetGuestCountInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SetGuestCountInput) => eventService.setGuestCount(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
    },
  })
}

/** Mark an inquiry lost; updates the detail cache and refreshes the lists and calendars. */
export function useLoseEvent(eventId: string): UseMutationResult<EventDetailDto, Error, LoseEventInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LoseEventInput) => eventService.lose(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: eventKeys.calendars() })
    },
  })
}

/** Reopen a lost inquiry; updates the detail cache and refreshes the lists and calendars. */
export function useReopenEvent(eventId: string): UseMutationResult<EventDetailDto, Error, ReopenEventInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ReopenEventInput) => eventService.reopen(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: eventKeys.calendars() })
    },
  })
}

/** Save an inquiry's requested dates; they hold nothing, so only the detail changes. */
export function useSetRequestedDates(eventId: string): UseMutationResult<EventDetailDto, Error, RequestedDatesInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RequestedDatesInput) => eventService.setRequestedDates(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
    },
  })
}

/** Hold a date for an inquiry; it shows on the calendar, so calendars refresh too. */
export function usePlaceHold(eventId: string): UseMutationResult<EventDetailDto, Error, HoldInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: HoldInput) => eventService.placeHold(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.calendars() })
    },
  })
}

/** Release an inquiry's hold; it leaves the calendar. */
export function useReleaseHold(eventId: string): UseMutationResult<EventDetailDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => eventService.releaseHold(eventId),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.calendars() })
    },
  })
}

/**
 * Permanently delete an event; drops the detail cache and refreshes the list.
 * `clients: 'remove'` also retires the client accounts this event was the last
 * of, so the users list is refreshed too.
 */
export function useDeleteEvent(): UseMutationResult<
  EventDeleteResultDto,
  Error,
  { eventId: string; clients?: DeleteEventQuery['clients'] }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { eventId: string; clients?: DeleteEventQuery['clients'] }) =>
      eventService.delete(vars.eventId, vars.clients),
    onSuccess: (_data, vars) => {
      queryClient.removeQueries({ queryKey: eventKeys.detail(vars.eventId) })
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      if (vars.clients === 'remove') void queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

/**
 * What clearing a venue's events would remove.
 *
 * Never cached beyond the dialog that asks: the confirmation is a promise about
 * what is there right now, and a stale count would understate what goes.
 */
export function useVenuePurgePreview(venueId: string, enabled: boolean): UseQueryResult<VenuePurgePreviewDto> {
  return useQuery({
    queryKey: eventKeys.venuePurge(venueId),
    queryFn: () => eventService.venuePurgePreview(venueId),
    enabled: enabled && venueId.length > 0,
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * Delete every event at one venue, and optionally its client contacts.
 *
 * Clears the event caches wholesale rather than surgically — every list, detail
 * and calendar window for the tenant may have just changed — and the user caches
 * too when accounts were asked to go with them.
 */
export function usePurgeVenue(): UseMutationResult<
  VenuePurgeResultDto,
  Error,
  { venueId: string; input: PurgeVenueEventsInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ venueId, input }) => eventService.purgeVenue(venueId, input),
    onSuccess: (_data, vars) => {
      queryClient.removeQueries({ queryKey: eventKeys.details() })
      void queryClient.invalidateQueries({ queryKey: eventKeys.all })
      void queryClient.invalidateQueries({ queryKey: venueKeys.all })
      if (vars.input.clients === 'remove') {
        void queryClient.invalidateQueries({ queryKey: userKeys.all })
        void queryClient.invalidateQueries({ queryKey: clientKeys.all })
      }
    },
  })
}

/** Lock or unlock a phase, refreshing the event so its frozen state updates. */
export function useSetPhaseLock(
  eventId: string,
): UseMutationResult<EventPhaseDto, Error, { phaseId: string; lock: boolean }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { phaseId: string; lock: boolean }) =>
      vars.lock ? eventService.lockPhase(eventId, vars.phaseId) : eventService.unlockPhase(eventId, vars.phaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) })
    },
  })
}

/** Add a phase to an event. */
export function useCreatePhase(eventId: string): UseMutationResult<EventDetailDto, Error, CreatePhaseInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePhaseInput) => eventService.createPhase(eventId, input),
    onSuccess: (data) => queryClient.setQueryData(eventKeys.detail(eventId), data),
  })
}

/** Update a phase's label, type, or time window. */
export function useUpdatePhase(
  eventId: string,
): UseMutationResult<EventDetailDto, Error, { phaseId: string; input: UpdatePhaseInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { phaseId: string; input: UpdatePhaseInput }) =>
      eventService.updatePhase(eventId, vars.phaseId, vars.input),
    onSuccess: (data) => queryClient.setQueryData(eventKeys.detail(eventId), data),
  })
}

/** Add a client-brought custom item ("Add your own") to the event. */
export function useAddBroughtIn(eventId: string): UseMutationResult<EventDetailDto, Error, AddBroughtInInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AddBroughtInInput) => eventService.addBroughtIn(eventId, input),
    onSuccess: (data) => queryClient.setQueryData(eventKeys.detail(eventId), data),
  })
}

/** Correct a brought-in piece: its name, class, shape, seats, or count. */
export function useUpdateBroughtIn(
  eventId: string,
): UseMutationResult<EventDetailDto, Error, { itemId: string; input: UpdateBroughtInInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { itemId: string; input: UpdateBroughtInInput }) =>
      eventService.updateBroughtIn(eventId, vars.itemId, vars.input),
    onSuccess: (data) => queryClient.setQueryData(eventKeys.detail(eventId), data),
  })
}

/** Remove a brought-in item from the event. */
export function useRemoveBroughtIn(
  eventId: string,
): UseMutationResult<void, Error, { itemId: string; confirmedPlans: ConfirmedPlansChoice }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { itemId: string; confirmedPlans: ConfirmedPlansChoice }) =>
      eventService.removeBroughtIn(eventId, vars.itemId, vars.confirmedPlans),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/** The planner's furniture gallery (venue-owned browsing + vendor-available). */
export function useFurnitureGallery(eventId: string, enabled = true): UseQueryResult<FurnitureGalleryDto> {
  return useQuery({
    queryKey: eventKeys.gallery(eventId),
    queryFn: () => eventService.furnitureGallery(eventId),
    enabled,
    staleTime: 30_000,
  })
}

/** Select how many of a vendor gallery piece this event rents (0 removes it).
 * Refreshes both the event detail (the returned value) and the gallery's
 * selected counts. */
export function useSetRental(
  eventId: string,
): UseMutationResult<
  EventDetailDto,
  Error,
  { itemId: string; quantity: number; confirmedPlans?: ConfirmedPlansChoice }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { itemId: string; quantity: number; confirmedPlans?: ConfirmedPlansChoice }) =>
      eventService.setRental(eventId, vars.itemId, vars.quantity, vars.confirmedPlans),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.gallery(eventId) })
    },
  })
}

/** Dry-run a legacy SVG import for one space of an event. */
export function usePreviewEventImport(
  eventId: string,
): UseMutationResult<EventImportPreviewDto, Error, PreviewEventImportInput> {
  return useMutation({
    mutationFn: (input: PreviewEventImportInput) => eventService.previewImport(eventId, input),
  })
}

/** Apply a legacy SVG import; refreshes event, furniture catalog, groups, venue inventory. */
export function useApplyEventImport(
  eventId: string,
): UseMutationResult<EventImportApplyResultDto, Error, ApplyEventImportInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ApplyEventImportInput) => eventService.applyImport(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data.event)
      void queryClient.invalidateQueries({ queryKey: eventKeys.gallery(eventId) })
      void queryClient.invalidateQueries({ queryKey: furnitureKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: furnitureGroupKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(data.event.venueId) })
    },
  })
}

/** Delete a phase. */
export function useDeletePhase(eventId: string): UseMutationResult<void, Error, { phaseId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { phaseId: string }) => eventService.deletePhase(eventId, vars.phaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) })
    },
  })
}

/** The companies planning the caller's events: the events list's Planner filter. */
export function useEventPlanners(enabled = true): UseQueryResult<EventPlannerOptionListDto> {
  return useQuery({ queryKey: eventKeys.planners(), queryFn: () => eventService.planners(), staleTime: 60_000, enabled })
}

/**
 * Put a company on the event as its planner. A new company is set up at the
 * event's venue too, so the venue's vendors and the planner filter are stale.
 */
export function useAssignEventVendor(eventId: string): UseMutationResult<EventDetailDto, Error, AssignEventVendorInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AssignEventVendorInput) => eventService.assignVendor(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: eventKeys.planners() })
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all })
    },
  })
}

/**
 * The venue's vendors, for the event's planner to pick from: every one that
 * offers the service, narrowed by name as they type. Off without an event.
 */
export function useVenueVendorChoices(
  eventId: string | null,
  q: string,
  service: VendorService,
): UseQueryResult<VenueVendorChoiceListDto> {
  const term = q.trim()
  return useQuery({
    queryKey: vendorKeys.eventChoices(eventId ?? '', term, service),
    queryFn: () => eventService.venueVendorChoices(eventId ?? '', { q: term, service }),
    enabled: eventId !== null,
    staleTime: 15_000,
  })
}

/** Take the company off the event. */
export function useRemoveEventVendor(eventId: string): UseMutationResult<EventDetailDto, Error, { assignmentId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ assignmentId }: { assignmentId: string }) => eventService.removeVendor(eventId, assignmentId),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: eventKeys.planners() })
    },
  })
}

/** What marking a client as a planner would do, for the company name typed so far. Only while asked for. */
export function usePlannerConversionPreview(
  eventId: string,
  clientId: string,
  companyName: string,
  enabled: boolean,
): UseQueryResult<ClientToPlannerDto> {
  const name = companyName.trim()
  return useQuery({
    queryKey: plannerConversionKeys.preview(eventId, clientId, name),
    queryFn: () => eventService.previewPlannerConversion(eventId, clientId, name === '' ? {} : { companyName: name }),
    enabled,
    staleTime: 0,
    // Never kept once the dialog closes: reopened, it must not offer to confirm
    // a warning read before someone else changed the events it describes.
    gcTime: 0,
    // The last answer stays up while the next name is checked, instead of the warning blinking out.
    placeholderData: keepPreviousData,
  })
}

/**
 * Mark a client as a planner. They leave every event's clients for its
 * planner slot, so every event read is stale, not just this one.
 */
export function useConvertClientToPlanner(
  eventId: string,
): UseMutationResult<ClientToPlannerDto, Error, { clientId: string; input: ClientToPlannerInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, input }: { clientId: string; input: ClientToPlannerInput }) =>
      eventService.convertClientToPlanner(eventId, clientId, input),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: plannerConversionKeys.all })
      void queryClient.invalidateQueries({ queryKey: eventKeys.all })
      void queryClient.invalidateQueries({ queryKey: clientKeys.all })
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all })
      // They are no longer one of the organization's people.
      void queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

/** Send the company contact's invitation again; the event shows when it went. */
export function useInviteEventVendorContact(eventId: string): UseMutationResult<void, Error, { assignmentId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ assignmentId }: { assignmentId: string }) => eventService.inviteVendorContact(eventId, assignmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) })
      void queryClient.invalidateQueries({ queryKey: eventKeys.emails(eventId) })
    },
  })
}

/** Search tenant client contacts (admin add-picker). Disabled until a query. */
export function useClientSearch(q: string): UseQueryResult<UserListDto> {
  return useQuery({
    queryKey: clientKeys.search(q.trim()),
    queryFn: () => eventService.searchClients(q.trim()),
    enabled: q.trim().length > 0,
    staleTime: 15_000,
  })
}

/** Add a client contact to an event (existing or new); refreshes the event. */
export function useAddEventClient(eventId: string): UseMutationResult<EventDetailDto, Error, AddEventClientInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AddEventClientInput) => eventService.addClient(eventId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(eventKeys.detail(eventId), data)
      void queryClient.invalidateQueries({ queryKey: clientKeys.all })
    },
  })
}

/** Remove a client contact from an event; refreshes the event. */
export function useRemoveEventClient(eventId: string): UseMutationResult<void, Error, { clientId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId }: { clientId: string }) => eventService.removeClient(eventId, clientId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/**
 * Invite (or re-invite) a client contact.
 *
 * The event is what shows "Send invitation" and the mail log's "nothing sent".
 * Both stay stale unless they are dropped, and the button then sends again.
 */
export function useInviteClient(eventId: string): UseMutationResult<void, Error, { clientId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId }: { clientId: string }) => eventService.inviteClient(clientId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) })
      void queryClient.invalidateQueries({ queryKey: eventKeys.emails(eventId) })
    },
  })
}

/**
 * Withdraw an invitation nobody accepted, removing the client contact. Offered only for a
 * PENDING invite: deactivating one would leave a row for someone who never
 * arrived and keep hold of their email address, so a mistyped invite could never
 * be re-sent to the corrected one.
 */
export function useDeleteInvitedClient(eventId: string): UseMutationResult<void, Error, { clientId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId }: { clientId: string }) => eventService.deletePendingClient(clientId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/** Deactivate a client contact; refreshes the event so its status updates. */
export function useDeactivateClient(eventId: string): UseMutationResult<UserDto, Error, { clientId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId }: { clientId: string }) => eventService.deactivateClient(clientId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/** Set or clear a client's phone; refreshes the event, which shows it. */
export function useSetClientPhone(
  eventId: string,
): UseMutationResult<UserDto, Error, { clientId: string; phone: string | null }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, phone }: { clientId: string; phone: string | null }) =>
      eventService.setClientPhone(clientId, { phone }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/** Reactivate a client contact; refreshes the event so its status updates. */
export function useReactivateClient(eventId: string): UseMutationResult<UserDto, Error, { clientId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId }: { clientId: string }) => eventService.reactivateClient(clientId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

// ---- documents ------------------------------------------------------------

/** Attach a document (URL or uploaded data URL); refreshes event detail. */
export function useCreateEventDocument(
  eventId: string,
): UseMutationResult<EventDocumentDto, Error, CreateEventDocumentInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEventDocumentInput) => eventService.createDocument(eventId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/** Remove a document; refreshes event detail. */
export function useDeleteEventDocument(
  eventId: string,
): UseMutationResult<void, Error, { documentId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId }) => eventService.deleteDocument(eventId, documentId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
  })
}

/**
 * One venue's calendar for one window.
 *
 * Keyed by the window as well as the venue, so paging back and forth through
 * months reads from cache instead of refetching the month you just left.
 */
/**
 * Every message sent to one event's client contacts.
 *
 * Not cached for long: the whole reason somebody opens it is that a client says
 * they did not get something, and a minute-old answer is the wrong one to give
 * them — the provider's webhook moves a row from `queued` to `delivered` or
 * `bounced` while the page is open.
 */
export function useEventEmailLog(eventId: string, enabled = true): UseQueryResult<EmailLogListDto> {
  return useQuery({
    queryKey: eventKeys.emails(eventId),
    queryFn: () => eventService.emailLog(eventId),
    enabled: enabled && eventId !== '',
    staleTime: 15_000,
  })
}

/**
 * Which of a venue's tracks are free for the window being planned.
 *
 * Keyed by the window, because the answer is only true for that window — so
 * changing a time re-asks rather than showing the last answer, and changing it
 * back reads from cache.
 *
 * A booking's own lane is excluded via `excludeEventId`, or moving its times
 * would report the lane as taken by itself.
 */
export function useTrackAvailability(
  query: TrackAvailabilityQuery,
  enabled = true,
): UseQueryResult<TrackAvailabilityListDto> {
  const dated = 'date' in query ? query : null
  return useQuery({
    queryKey: eventKeys.trackAvailability(
      query.venueId,
      dated?.date ?? null,
      dated?.startsAt ?? null,
      dated?.endsAt ?? null,
      query.excludeEventId ?? null,
    ),
    queryFn: () => eventService.trackAvailability(query),
    enabled: enabled && query.venueId.length > 0,
    // An answer that is a few seconds old is worse than useless here: somebody
    // else may have taken the lane, and the write is what actually decides.
    staleTime: 0,
  })
}

export function useVenueCalendar(query: VenueCalendarQuery, enabled = true): UseQueryResult<VenueCalendarDto> {
  return useQuery({
    queryKey: eventKeys.calendar(query.venueId, query.from, query.to),
    queryFn: () => eventService.calendar(query),
    enabled: enabled && query.venueId.length > 0,
  })
}
